import React, { ReactElement } from 'react';

import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { FetchAPI, FieldAPI, FilterAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants/lib';
import Controller, { OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { ContractsAliases as Aliases } from '../../codegen/constants/aliases/contracts-aliases';
import { Route } from '../../route';
import BaseController from '../BaseController';

enum ContractStatus {
  INACTIVE = 'I',
  FUTURE_VALIDITY = 'F',
  CURENT = 'A',
  EXPIRED = 'X',
}

interface Contract {
  nrSeqContract: number,
  ieStatus: ContractStatus,
  dsStatus: string,
  expirationDate: string,
}

@Controller({ fragment: FragmentNames.CONTRACTS })
class ContractsController extends BaseController {

  @OnMount([Aliases.FETCH_CONTRACTS, Aliases.FETCH_MESSAGE])
  onMountFetch(event: MountEvent): void {
    const currentProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const api = event.target as FetchAPI;
    api.setParameters({ nrSeqProvider: currentProvider });
    api.forceFetch();
  }

  @OnMount(Aliases.FILTER)
  onMountFilterContracts(event: MountEvent): void {
    const api = event.target as FilterAPI;
    const currentProvider = providerContext.getSelectedProvider().nrSeqProvider;
    api.setData({ nrSeqProvider: currentProvider });
  }

  @OnMount(Aliases.FIELD_EXPIRATION)
  onMountFieldExpiration(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { expirationDate } = api.getData() as unknown as Contract;
    api.setVisible(!isEmptyOrBlankString(expirationDate));
  }

  @OnSelectItem(Aliases.DECK_CONTRACTS)
  onSelectitemAccreditationCategories(event: SelectItemEvent<{ nrSeqContract: number }>): void {
    const { nrSeqContract } = event.item;
    this.historyService.push(Route.CONTRACT_DETAILS, { nrSeqContract });
  }

  @OnDynamicContentRender(Aliases.CARD_CONTRACT)
  onDynamicContentRenderCardCall(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as Contract;
    const content = (): ReactElement => (this.getStampByStatus(ieStatus, dsStatus));
    render(content);
  }

  private getStampByStatus(ieStatus: ContractStatus, label: string): ReactElement {
    let stampProps = null;
    const icon = {
      [ContractStatus.INACTIVE]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label },
      [ContractStatus.FUTURE_VALIDITY]: { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label },
      [ContractStatus.CURENT]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
      [ContractStatus.EXPIRED]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label },
    };

    stampProps = icon[ieStatus];
    return <Stamp {...stampProps} />;
  }

}

export {
  ContractsController,
};
